<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import router from '@/router'
import { useRoute } from 'vue-router'
import FormViewer from '@/domains/home/services/nir/forms/components/FormViewer.vue'
import api from '@/app/services/apiNir.ts'
import type {AxiosError, AxiosResponse} from 'axios'
import AppModal from '@/components/ui/AppModal.vue'
import {useFormsStore} from "@/domains/home/services/nir/configurations/stores/forms.ts";
import {FormDataSection, FormDataSectionField} from "@/domains/home/services/nir/configurations/types/forms.ts";
import {ApiErrorResponse} from "@/app/types";
import {useNotificationsStore} from "@/app/notify/notifications.ts";

const store = useFormsStore()

// Type definitions first
type FormSection = {
  formId: string
  name: string
  description: string
  position: number
}

type UpdateFormSection = {
  id: string
  formId: string
  name: string
  description: string
  position: number
}

type FormField = {
  sectionId: string
  formId: string
  fieldId: string
  label: string
  placeholder: string
  isMandatory: boolean
  isActive: boolean
  position: number
}

type UpdateFormField = {
  id: string
  formId: string
  fieldId: string
  label: string
  placeholder: string
  isMandatory: boolean
  isActive: boolean
}

type FieldRestriction = {
  id: string
  formId: string
  min: number | null
  max: number | null
  regex: string | null
}

type FormAttachment = {
  formId: string
  label: string
  description: string
  isMandatory: boolean
  maxSize: number
  fileType: string
}

type UpdateFormAttachment = {
  id: string
  formId: string
  label: string
  description: string
  isMandatory: boolean
  maxSize: number
  fileType: string
}

type CreateFormStep = {
  formId: string
  name: string
  position: number
}

// Refs
const loading = ref(false)
const sectionLoading = ref(false)
const fieldLoading = ref(false)
const attachmentLoading = ref(false)
const showSectionForm = ref(false)
const selectedFieldId = ref('')
const selectedSectionId = ref('')
const showFieldForm = ref<string | null>(null)
const showRemoveFieldForm = ref(false)
const showRemoveSectionForm = ref(false)
const showEditFieldForm = ref(false)
const showEditSectionForm = ref(false)
const showFieldRestrictionForm = ref(false)
const isEditingRestrictions = ref(false)
const viewMode = ref<'editor' | 'preview'>('editor')
const activeTab = ref<string>('')
const showAttachmentForm = ref(false)
const showEditAttachmentForm = ref(false)
const showRemoveAttachmentForm = ref(false)
const selectedAttachmentId = ref('')

// Steps state
const showStepForm = ref(false)
const stepLoading = ref(false)
const draggedSectionId = ref<string | null>(null)
const showDeleteStepConfirm = ref(false)
const selectedStepId = ref<string | null>(null)

const notify = useNotificationsStore()

const availableFileTypes = [
  { value: 'application/pdf', label: 'PDF' },
  { value: 'image/png', label: 'PNG' },
  { value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', label: 'DOCX' },
]

const sectionForm = ref<FormSection>({
  formId: '',
  name: '',
  description: '',
  position: 0,
})

const editSectionForm = ref<UpdateFormSection>({
  id: '',
  formId: '',
  name: '',
  description: '',
  position: 0,
})

const fieldForm = ref<FormField>({
  sectionId: '',
  formId: '',
  fieldId: '',
  placeholder: '',
  label: '',
  isMandatory: false,
  isActive: true,
  position: 0,
})

const editFieldForm = ref<UpdateFormField>({
  id: '',
  formId: '',
  fieldId: '',
  placeholder: '',
  label: '',
  isMandatory: false,
  isActive: true,
})

const fieldRestrictionForm = ref<FieldRestriction>({
  id: '',
  formId: '',
  min: null,
  max: null,
  regex: null,
})

const attachmentForm = ref<FormAttachment>({
  formId: '',
  label: '',
  description: '',
  isMandatory: false,
  maxSize: 5,
  fileType: '',
})

const updateAttachmentForm = ref<UpdateFormAttachment>({
  id: '',
  formId: '',
  label: '',
  description: '',
  isMandatory: false,
  maxSize: 5,
  fileType: '',
})

const stepForm = ref<CreateFormStep>({
  formId: '',
  name: '',
  position: 0,
})

const formId = ref('')
const route = useRoute()

// Computed property to get sorted sections
const orderedSections = computed(() => {
  if (!store.formData?.sections) return []
  return [...store.formData.sections].sort((a, b) => a.position - b.position)
})

// Computed property to get sorted steps
const orderedSteps = computed(() => {
  if (!store.formSteps) return []
  return [...store.formSteps].sort((a, b) => a.position - b.position)
})

function fetch() {
  loading.value = true
  store.fetchFormFields(formId.value).then(() => {
    loading.value = false
  })
  store.fetchFormDefinition(formId.value)
}

function reload() {
  store.fetchFormFields(formId.value)
  store.fetchFormDefinition(formId.value)
}

function openSectionForm() {
  showSectionForm.value = true
  sectionForm.value = {
    formId: formId.value,
    name: '',
    description: '',
    position: store.formData?.sections?.length || 0,
  }
}

function closeSectionForm() {
  showSectionForm.value = false
}

function closeEditSectionForm(){
  showEditSectionForm.value = false
}

function toggleViewMode() {
  viewMode.value = viewMode.value === 'editor' ? 'preview' : 'editor'
}

function createSection() {
  sectionLoading.value = true
  store
    .createFormSection(sectionForm.value)
    .then(() => {
      sectionLoading.value = false
      closeSectionForm()
      fetch()
    })
    .catch(() => {
      sectionLoading.value = false
    })
}

function updateSection(){
  sectionLoading.value = true
  store
      .updateFormSection(editSectionForm.value)
      .then(() => {
        sectionLoading.value = false
        closeEditSectionForm()
        fetch()
      })
      .catch(() => {
        sectionLoading.value = false
      })
}

function openFieldForm(sectionId: string) {
  showFieldForm.value = sectionId
  const section = store.formData?.sections?.find((s) => s.id === sectionId)
  fieldForm.value = {
    sectionId: sectionId,
    formId: formId.value,
    fieldId: '',
    label: '',
    placeholder: '',
    isMandatory: false,
    isActive: true,
    position: section?.fields?.length || 0,
  }
}

function closeFieldForm() {
  showFieldForm.value = null
}

function createField() {
  fieldLoading.value = true
  store
    .createFormField(fieldForm.value)
    .then(() => {
      fieldLoading.value = false
      closeFieldForm()
      fetch()
    })
    .catch(() => {
      fieldLoading.value = false
    })
}

function removeField() {
  fieldLoading.value = true
  let payload = {
    fieldId: selectedFieldId.value,
    formId: formId.value,
  }
  store
    .removeFormField(payload)
    .then(() => {
      fieldLoading.value = false
      showRemoveFieldForm.value = false
      fetch()
    })
    .catch(() => {
      fieldLoading.value = false
    })
}

function removeSection() {
  fieldLoading.value = true
  let payload = {
    sectionId: selectedSectionId.value,
    formId: formId.value,
  }
  store
      .removeFormSection(payload)
      .then(() => {
        fieldLoading.value = false
        showRemoveSectionForm.value = false
        fetch()
      })
      .catch(() => {
        fieldLoading.value = false
      })
}

function getSortedFields(fields: any[]) {
  if (!fields) return []
  return [...fields].sort((a, b) => a.position - b.position)
}

function getSortedOptions(options: any[]) {
  if (!options || !Array.isArray(options)) return []
  return [...options].sort((a, b) => a.position - b.position)
}

function back() {
  router.push({ name: 'nir-forms-list' })
}

function downloadTemplate() {
  download()
}

const download = async () => {
  const path = `/v1/configurations/forms/template?formId=${formId.value}`

  try {
    const response: AxiosResponse<Blob> = await api.get(path, {
      responseType: 'blob',
    })

    // Create a blob from the response
    const blob = new Blob([response.data], {
      type:
        response.headers['content-type'] ||
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })

    // Create a temporary URL for the blob
    const url = window.URL.createObjectURL(blob)

    // Create a temporary anchor element to trigger download
    const link = document.createElement('a')
    link.href = url

    // Extract filename from Content-Disposition header if available
    const contentDisposition = response.headers['content-disposition']
    let filename = 'form-template.xlsx'

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }

    // Use form name if available
    if (store.form?.name) {
      filename = `${store.form.name}-template.xlsx`
    }

    link.download = filename

    // Append to body, click, and remove
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up the blob URL
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error downloading template:', error)
    // You could add a toast notification here to inform the user of the error
  }
}

function setEditorMode() {
  viewMode.value = 'editor'
}

function editField(field: FormDataSectionField) {
  editFieldForm.value = {
    id: field.id,
    formId: formId.value,
    fieldId: field.field.id,
    label: field.label || '',
    placeholder: field.field.placeholder,
    isMandatory: field.field.isMandatory,
    isActive: field.field.isActive,
  }
  showEditFieldForm.value = true
}

function editSection(section: FormDataSection) {
  editSectionForm.value = {
    id: section.id,
    formId: formId.value,
    name: section.name,
    description: section.description,
    position: section.position
  }
  showEditSectionForm.value = true
}

function closeEditFieldForm() {
  showEditFieldForm.value = false
}

function updateField() {
  fieldLoading.value = true
  store.updateFormField(editFieldForm.value)
    .then(() => {
      fieldLoading.value = false
      closeEditFieldForm()
      fetch()
    })
    .catch(() => {
      fieldLoading.value = false
    })
}

function closeFieldRestrictionForm() {
  showFieldRestrictionForm.value = false
}

function saveFieldRestrictions() {
  fieldLoading.value = true

  if (isEditingRestrictions.value) {
    store.updateFormFieldRestriction(fieldRestrictionForm.value)
        .then(() => {
          fieldLoading.value = false
          closeFieldRestrictionForm()
          fetch()
        })
        .catch(() => {
          fieldLoading.value = false
        })
  }else {
    store.createFormFieldRestriction(fieldRestrictionForm.value)
        .then(() => {
          fieldLoading.value = false
          closeFieldRestrictionForm()
          fetch()
        })
        .catch(() => {
          fieldLoading.value = false
        })
  }

}

function deleteField(id: string) {
  selectedFieldId.value = id
  showRemoveFieldForm.value = true
}

function deleteSection(id: string) {
  selectedSectionId.value = id
  showRemoveSectionForm.value = true
}

function loadRestrictions(){
  fieldForm.value.isMandatory = store.fields?.find((field) => field.id == fieldForm.value.fieldId)?.isMandatory || false
  fieldForm.value.isActive = store.fields?.find((field) => field.id == fieldForm.value.fieldId)?.isActive || false
}

function openAttachmentForm() {
  showAttachmentForm.value = true
  attachmentForm.value = {
    formId: formId.value,
    label: '',
    description: '',
    isMandatory: false,
    maxSize: 5,
    fileType: '',
  }
}

function closeAttachmentForm() {
  showAttachmentForm.value = false
}

function createAttachment() {
  if (!attachmentForm.value.fileType) {
    notify.error('Please select a file type')
    return
  }
  attachmentLoading.value = true
  store
    .createFormAttachment(attachmentForm.value)
    .then(() => {
      attachmentLoading.value = false
      closeAttachmentForm()
      fetch()
    })
    .catch((error:AxiosError<ApiErrorResponse>) => {
      attachmentLoading.value = false
      notify.error(error.response?.data?.error.error ?? "Error creating attachment");
    })
}

function updateAttachment() {
  if (!updateAttachmentForm.value.fileType) {
    notify.error('Please select a file type')
    return
  }
  attachmentLoading.value = true
  store
      .updateFormAttachment(updateAttachmentForm.value)
      .then(() => {
        attachmentLoading.value = false
        closeEditAttachmentForm()
        fetch()
      })
      .catch((error:AxiosError<ApiErrorResponse>) => {
        attachmentLoading.value = false
        notify.error(error.response?.data?.error.error ?? "Error updating attachment");
      })
}

function editAttachment(attachment: any) {
  updateAttachmentForm.value = {
    id: attachment.id,
    formId: formId.value,
    label: attachment.label,
    description: attachment.description || '',
    isMandatory: attachment.isMandatory,
    maxSize: attachment.maxSize || 5,
    fileType: attachment.fileType || '',
  }
  showEditAttachmentForm.value = true
}

function closeEditAttachmentForm() {
  showEditAttachmentForm.value = false
}

function deleteAttachment(id: string) {
  selectedAttachmentId.value = id
  showRemoveAttachmentForm.value = true
}

function removeAttachment() {
  attachmentLoading.value = true
  let payload = {
    id: selectedAttachmentId.value,
    formId: formId.value,
  }
  store
    .removeFormAttachment(payload)
    .then(() => {
      attachmentLoading.value = false
      showRemoveAttachmentForm.value = false
      fetch()
    })
    .catch((error:AxiosError<ApiErrorResponse>) => {
      attachmentLoading.value = false
      notify.error(error.response?.data?.error.error ?? "Error removing attachment");
    })
}

function select(name:string) {
  activeTab.value = name
  localStorage.setItem('tab', name)
}

function moveSection(section: FormDataSection, isUp: boolean) {
  let payload = {
    formId: formId.value,
    sectionId: section.id,
    position: isUp ? section.position - 1 : section.position + 1,
  }
  store.moveSection(payload)
      .then(() => {
        reload()
      })
}

function moveField(field: FormDataSectionField, isUp: boolean){
  console.log(field)
  let payload = {
    formId: formId.value,
    fieldId: field.field.id,
    position: isUp ? field.position - 1 : field.position + 1,
  }
  store.moveField(payload)
      .then(() => {
        reload()
      })
}

// Steps Management Functions
function openStepForm() {
  showStepForm.value = true
  stepForm.value = {
    formId: formId.value,
    name: '',
    position: orderedSteps.value.length,
  }
}

function closeStepForm() {
  showStepForm.value = false
}

function createStep() {
  stepLoading.value = true
  store.createFormStep(stepForm.value)
    .then(() => {
      stepLoading.value = false
      closeStepForm()
      store.fetchFormSteps(formId.value)
    })
    .catch(() => {
      stepLoading.value = false
    })
}

function moveStepPosition(step: any, isUp: boolean) {
  const payload = {
    formId: formId.value,
    stepId: step.id,
    position: isUp ? step.position - 1 : step.position + 1,
  }
  store.moveStep(payload)
    .then(() => {
      store.fetchFormSteps(formId.value)
    })
}

function confirmDeleteStep(stepId: string) {
  selectedStepId.value = stepId
  showDeleteStepConfirm.value = true
}

function deleteStep() {
  if (!selectedStepId.value) return
  stepLoading.value = true
  store.removeStep({ stepId: selectedStepId.value })
    .then(() => {
      stepLoading.value = false
      showDeleteStepConfirm.value = false
      selectedStepId.value = null
      store.fetchFormSteps(formId.value)
    })
    .catch(() => {
      stepLoading.value = false
    })
}

// Drag and drop for sections
function onSectionDragStart(sectionId: string) {
  draggedSectionId.value = sectionId
}

function onSectionDragEnd() {
  draggedSectionId.value = null
}

function onDropSectionToStep(stepId: string) {
  if (!draggedSectionId.value) return
  const payload = {
    sectionId: draggedSectionId.value,
    stepId: stepId,
  }
  store.assignSectionToStep(payload)
    .then(() => {
      draggedSectionId.value = null
      store.fetchFormSteps(formId.value)
      fetch()
    })
}

function getUnassignedSections() {
  const assignedSectionIds = orderedSteps.value.flatMap(step => step.sections?.map((s: any) => s.id) || [])
  return orderedSections.value.filter(section => !assignedSectionIds.includes(section.id))
}

function getSectionsForStep(step: any) {
  return step.sections || []
}

onMounted(() => {
  formId.value = route.params.id as string
  store.fetchForm(formId.value)
  store.fetchFields(1, 1000)
  store.fetchFormSteps(formId.value)

  activeTab.value = localStorage.getItem('tab') || 'fields'
  fetch()
})
</script>

<template>
  <div class="w-full mx-auto p-6">
    <!-- Header -->
    <div class="mb-6 p-2 space-y-3">
      <div class="flex items-center justify-between">
        <button class="btn-primary text-primary-600 p-2 rounded bg-primary-100" @click="back">
          <i class="fa-solid fa-arrow-left"></i> Back to Forms
        </button>

        <!-- View Mode Toggle -->
        <div class="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
          <button
            @click="viewMode = 'editor'"
            :class="[
              'px-4 py-2 rounded-md text-sm font-medium transition',
              viewMode === 'editor'
                ? 'bg-white text-primary shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            ]"
          >
            <i class="fa-solid fa-pen-to-square mr-2"></i>
            Editor
          </button>
          <button
            @click="viewMode = 'preview'"
            :class="[
              'px-4 py-2 rounded-md text-sm font-medium transition',
              viewMode === 'preview'
                ? 'bg-white text-primary shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            ]"
          >
            <i class="fa-solid fa-eye mr-2"></i>
            Preview
          </button>
        </div>
      </div>

      <div v-if="store.form" class="bg-white rounded-lg shadow p-6 border border-gray-200">
        <h1 class="text-2xl font-bold text-gray-800">{{ store.form.name }}</h1>
        <p class="text-gray-600 mt-2">{{ store.form.description }}</p>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <i class="fa-solid fa-spinner fa-spin text-3xl text-primary-600"></i>
      <p class="text-gray-600 mt-2">Loading form data...</p>
    </div>

    <!-- Form Composer -->
    <div v-else class="space-y-6">
      <!-- Preview Mode -->
      <div v-if="viewMode === 'preview'" class="bg-white rounded-lg shadow border border-gray-200 p-6">
        <div class="border-t border-gray-200 pt-4">
          <FormViewer :form-definition="store.formDefinition" :active="true" />
        </div>
      </div>

      <!-- Editor Mode -->
      <template v-else>
      <!-- Tabs -->
      <div class="border-b border-gray-200">
        <nav class="flex space-x-8">
          <label
            @click="select('fields')"
            :class="[
              'py-3 px-1 border-b-2 font-medium text-sm transition cursor-pointer',
              activeTab === 'fields'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300']">
            <i class="fa-solid fa-list-check mr-2"></i>
            Form Fields
          </label>
          <label
            @click="select('attachments')"
            :class="[
              'py-3 px-1 border-b-2 font-medium text-sm transition cursor-pointer',
              activeTab === 'attachments'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300']">
            <i class="fa-solid fa-paperclip mr-2"></i>
            Attachments
            <span v-if="store.formData?.attachments?.length" class="ml-2 bg-primary-100 text-primary px-2 py-0.5 rounded-full text-xs">
              {{ store.formData.attachments.length }}
            </span>
          </label>
          <label
            @click="select('steps')"
            :class="[
              'py-3 px-1 border-b-2 font-medium text-sm transition cursor-pointer',
              activeTab === 'steps'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300']">
            <i class="fa-solid fa-layer-group mr-2"></i>
            Steps
            <span v-if="orderedSteps.length" class="ml-2 bg-primary-100 text-primary px-2 py-0.5 rounded-full text-xs">
              {{ orderedSteps.length }}
            </span>
          </label>
        </nav>
      </div>

      <!-- Fields Tab Content -->
      <div v-if="activeTab === 'fields'">
        <!-- Create Section Button -->
        <div class="flex justify-end gap-2 mb-4">
          <button
            @click="openSectionForm"
            class="btn-primary bg-primary-600 text-white px-4 py-1 rounded hover:bg-primary-700 transition"
          >
            <i class="fa-solid fa-plus"></i> Section
          </button>
        </div>

      <!-- Sections List -->
      <div v-if="orderedSections.length > 0" class="space-y-4">
        <div
          v-for="(section, sectionIndex) in orderedSections"
          :key="section.id"
          class="bg-white shadow rounded">

          <!-- Section Header -->
          <div class="bg-gray-50 px-6 py-4 border-b border-gray-100">
            <div class="flex flex-col">
              <div class="w-full">
                <div class="flex items-center gap-2">
                  <h3 class="text-lg font-semibold text-gray-800"> <b class="bg-gray-500 text-white rounded-lg px-2 py-1"> {{section.position + 1}}</b> {{ section.name }}</h3>
                </div>
                <p class="text-gray-600 mt-1">{{ section.description }}</p>
              </div>
              <div class="flex justify-end gap-2  pt-3">
                <button
                  @click="openFieldForm(section.id)"
                  class="btn-sm-primary">
                  <i class="fa-solid fa-plus"></i> Field
                </button>

                <button
                    @click="editSection(section)"
                    class="btn-sm-primary">
                  <i class="fas fa-pen"></i>
                </button>

                <button
                    @click="deleteSection(section.id)"
                    class="btn-sm-danger">
                  <i class="fas fa-trash"></i>
                </button>

                <button class="btn-sm-primary" @click="moveSection(section, true)" :disabled="section.position == 0">
                  <i class="fas fa-angle-up"></i>
                </button>
                <button class="btn-sm-primary" @click="moveSection(section, false)" :disabled="section.position == orderedSections.length - 1">
                  <i class="fas fa-angle-down"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Fields List -->
          <div class="px-6 py-4">
            <div v-if="section.fields && section.fields.length > 0" class="space-y-3">
              <div
                v-for="(fieldData, fieldIndex) in getSortedFields(section.fields)"
                :key="fieldData.field.id"
                class="p-3 bg-gray-50 rounded border border-gray-200 hover:bg-primary-100 hover:border-2 hover:border-red-200">
                <div class="flex items-center gap-4">
                  <span class="text-gray-500 font-mono text-sm"><b class="bg-gray-500 text-white rounded-lg px-2 py-1"> {{fieldData.position + 1}}</b> </span>
                  <div class="flex-1">
                    <div class="flex items-center gap-2">
                      <span class="font-medium text-gray-800">{{ fieldData.field.label }}</span>
                      <span class="font-medium text-gray-500" v-if="fieldData.label"> - {{ fieldData.label }}</span>
                      <i v-if="fieldData.field.isMandatory" class="text-red-500 text-xs fas fa-asterisk"></i>
                      <i class="fas fa-check text-green-600 text-xs"  v-if="fieldData.field.isActive"></i>
                      <i class="fas fa-ban text-gray-600 text-xs"  v-else></i>
                    </div>
                    <div class="text-sm text-gray-600">
                      <span class="font-mono bg-primary-200 text-primary-600 px-2 py-0.5 rounded">{{ fieldData.field.name }}</span>
                      <span class="mx-2">•</span>
                      <span class="text-primary-600">{{ fieldData.field.type }}</span>
                    </div>

                    <div
                      class="text-sm my-2 text-gray-600 border border-gray-100 rounded-lg p-2"
                      v-if="fieldData.field.type == 'select' || fieldData.field.type == 'radio' || fieldData.field.type == 'checkbox'">
                      <div
                        class="text-sm text-gray-600 py-1"
                        v-for="(option, idx) in fieldData.field.options"
                        :key="idx">
                        <span class="font-mono bg-gray-200 px-2 py-0.5 rounded">{{option.label }}</span>
                        <span class="mx-2">•</span>
                        <span class="text-blue-600">{{ option.value }}</span>

                        <span
                          v-if="option.isActive"
                          class="text-xs bg-green-100 text-green-800 px-2 py-1 rounded mx-1">
                          <i class="fas fa-check"></i>
                        </span>
                        <span
                          v-else
                          class="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded mx-1">
                          <i class="fa fa-ban"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">

                    <button
                        @click="editField(fieldData)"
                        class="btn-sm-primary">
                      <i class="fas fa-pen"></i>
                    </button>

                    <button
                      @click="deleteField(fieldData.field.id)"
                      class="btn-sm-danger">
                      <i class="fas fa-trash"></i>
                    </button>

                    <button class="btn-sm-primary" @click="moveField(fieldData, true)" :disabled="fieldData.position == 0">
                      <i class="fas fa-angle-up"></i>
                    </button>
                    <button class="btn-sm-primary" @click="moveField(fieldData, false)" :disabled="fieldData.position == section.fields.length - 1">
                      <i class="fas fa-angle-down"></i>
                    </button>
                  </div>
                </div>

                <!-- Display Options for SELECT, RADIO, CHECKBOX fields -->
                <div
                  v-if="
                    (fieldData.field.type === 'SELECT' ||
                      fieldData.field.type === 'DROPDOWN' ||
                      fieldData.field.type === 'RADIO' ||
                      fieldData.field.type === 'CHECKBOX') &&
                    fieldData.field.options &&
                    fieldData.field.options.length > 0
                  "
                  class="mt-3 ml-8 pl-4 border-l-2 border-gray-300"
                >
                  <div class="text-xs font-semibold text-gray-500 mb-2">
                    <i class="fa-solid fa-list mr-1"></i>Options:
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <span
                      v-for="option in getSortedOptions(fieldData.field.options)"
                      :key="option.id"
                      class="text-xs px-2 py-1 rounded"
                      :class="
                        option.isActive
                          ? 'bg-primary-100 text-blue-800'
                          : 'bg-gray-200 text-gray-600 line-through'
                      "
                    >
                      {{ option.label }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-8 text-gray-500">
              <i class="fa-solid fa-inbox text-3xl mb-2"></i>
              <p>No fields in this section. Click "Add Field" to get started.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="bg-white rounded-lg border border-gray-200 shadow-md p-12 text-center">
        <i class="fa-solid fa-folder-open text-5xl text-gray-300 mb-4"></i>
        <h3 class="text-xl font-semibold text-gray-700 mb-2">No sections yet</h3>
        <p class="text-gray-600 mb-6">Create your first section to start building your form</p>
        <button
          @click="openSectionForm"
          class="btn-primary bg-primary-600 text-white px-6 py-3 rounded hover:bg-primary-700 transition"
        >
          <i class="fa-solid fa-plus mr-2"></i> Create First Section
        </button>
      </div>
      </div>

      <!-- Attachments Tab Content -->
      <div v-if="activeTab === 'attachments'">
        <!-- Add Attachment Button -->
        <div class="flex justify-end gap-2 mb-4">
          <button
            @click="openAttachmentForm"
            class="btn-primary bg-primary-600 text-white px-4 py-1 rounded hover:bg-primary-700 transition"
          >
            <i class="fa-solid fa-plus"></i> Add Attachment
          </button>
        </div>

        <!-- Attachments List -->
        <div v-if="store.formData?.attachments && store.formData.attachments.length > 0" class="bg-white rounded-lg shadow border border-gray-200">
          <div class="px-6 py-4 border-b border-gray-100 bg-gray-50">
            <h3 class="text-lg font-semibold text-gray-800">
              <i class="fa-solid fa-paperclip mr-2 text-gray-500"></i>
              Required Attachments
            </h3>
            <p class="text-sm text-gray-600 mt-1">Documents that applicants need to upload with their submission</p>
          </div>
          <div class="divide-y divide-gray-100">
            <div
              v-for="attachment in store.formData.attachments"
              :key="attachment.id"
              class="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition"
            >
              <div class="flex items-center gap-4">
                <div class="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <i class="fa-solid fa-file-alt text-primary"></i>
                </div>
                <div>
                  <div class="flex items-center gap-2">
                    <span class="font-medium text-gray-800">{{ attachment.label }}</span>
                    <span
                      v-if="attachment.isMandatory"
                      class="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700"
                    >
                      <i class="fa-solid fa-asterisk text-[8px] mr-1"></i>Required
                    </span>
                    <span
                      v-else
                      class="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600"
                    >
                      Optional
                    </span>
                  </div>
                  <p v-if="attachment.description" class="text-sm text-gray-500 mt-1">{{ attachment.description }}</p>
                  <div class="flex gap-2 py-2">
                    <label class="bg-gray-100 text-gray-600 px-2 rounded-lg text-xs">{{attachment.maxSize}} MB</label>
                    <label class="bg-gray-100 text-gray-600 px-2 rounded-lg text-xs">{{attachment.fileType}}</label>
                  </div>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <button
                  @click="editAttachment(attachment)"
                  class="btn-sm-primary"
                  title="Edit attachment"
                >
                  <i class="fa-solid fa-pen"></i>
                </button>
                <button
                  @click="deleteAttachment(attachment.id)"
                  class="btn-sm-danger"
                  title="Remove attachment"
                >
                  <i class="fa-solid fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State for Attachments -->
        <div v-else class="bg-white rounded-lg border border-gray-200 shadow-md p-12 text-center">
          <i class="fa-solid fa-paperclip text-5xl text-gray-300 mb-4"></i>
          <h3 class="text-xl font-semibold text-gray-700 mb-2">No attachments defined</h3>
          <p class="text-gray-600 mb-6">Define the documents that applicants need to upload with their submission</p>
          <button
            @click="openAttachmentForm"
            class="btn-primary bg-primary-600 text-white px-6 py-3 rounded hover:bg-primary-700 transition"
          >
            <i class="fa-solid fa-plus mr-2"></i> Add First Attachment
          </button>
        </div>
      </div>

      <!-- Steps Tab Content -->
      <div v-if="activeTab === 'steps'" class="space-y-6">
        <!-- Unassigned Sections Pool -->
        <div v-if="getUnassignedSections().length > 0" class="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div class="flex items-center gap-2 mb-3">
            <i class="fa-solid fa-folder-open text-gray-500"></i>
            <h4 class="font-semibold text-gray-700">Unassigned Sections</h4>
            <span class="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
              {{ getUnassignedSections().length }}
            </span>
          </div>
          <p class="text-sm text-gray-500 mb-3">Drag sections below to assign them to a step</p>
          <div class="flex flex-wrap gap-2">
            <div
              v-for="section in getUnassignedSections()"
              :key="section.id"
              draggable="true"
              @dragstart="onSectionDragStart(section.id)"
              @dragend="onSectionDragEnd"
              class="px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg cursor-move hover:bg-primary-100 transition flex items-center gap-2"
            >
              <i class="fa-solid fa-grip-vertical text-gray-400"></i>
              <span class="text-sm font-medium text-primary-800">{{ section.name }}</span>
            </div>
          </div>
        </div>

        <!-- Desktop-like Step Cards Container -->
        <div class="bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl p-6 min-h-[400px] border border-slate-300 shadow-inner">
          <!-- Header -->
          <div class="flex items-center justify-between mb-6">
            <div>
              <h3 class="text-lg font-semibold text-gray-800">
                <i class="fa-solid fa-layer-group mr-2 text-primary"></i>
                Form Steps
              </h3>
              <p class="text-sm text-gray-600 mt-1">Create steps and drag sections to organize your form workflow.</p>
            </div>
            <div class="text-sm text-gray-500">
              <span class="bg-white px-3 py-1 rounded-full shadow-sm">
                {{ orderedSteps.length }} steps
              </span>
            </div>
          </div>

          <!-- Steps Grid (Desktop-like tiles) -->
          <div class="flex gap-4 overflow-x-auto pb-4 min-h-[300px]">
            <!-- Add Step Card (Always First) -->
            <div
              @click="openStepForm"
              class="flex-shrink-0 w-64 min-h-[280px] bg-white/50 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-white/80 hover:border-primary transition-all group"
            >
              <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-primary-100 transition">
                <i class="fa-solid fa-plus text-2xl text-gray-400 group-hover:text-primary transition"></i>
              </div>
              <span class="text-gray-500 font-medium group-hover:text-primary transition">Add New Step</span>
              <span class="text-xs text-gray-400 mt-1">Click to create</span>
            </div>

            <!-- Step Cards -->
            <div
              v-for="(step, stepIndex) in orderedSteps"
              :key="step.id"
              @dragover.prevent
              @drop="onDropSectionToStep(step.id)"
              :class="[
                'flex-shrink-0 w-64 min-h-[280px] bg-white rounded-xl shadow-lg border-2 flex flex-col overflow-hidden transition-all hover:shadow-xl',
                draggedSectionId ? 'border-primary border-dashed' : 'border-gray-200'
              ]"
            >
              <!-- Step Header -->
              <div class="bg-gradient-to-r from-primary-500 to-primary-600 px-4 py-3 text-white">
                <div class="flex items-center justify-between">
                  <span class="bg-white/20 px-2 py-0.5 rounded text-xs font-bold">
                    #{{ stepIndex + 1 }}
                  </span>
                  <div class="flex items-center gap-1">
                    <button
                      @click="moveStepPosition(step, true)"
                      :disabled="stepIndex === 0"
                      class="p-1 hover:bg-white/20 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move left"
                    >
                      <i class="fa-solid fa-chevron-left text-xs"></i>
                    </button>
                    <button
                      @click="moveStepPosition(step, false)"
                      :disabled="stepIndex === orderedSteps.length - 1"
                      class="p-1 hover:bg-white/20 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move right"
                    >
                      <i class="fa-solid fa-chevron-right text-xs"></i>
                    </button>
                    <button
                      @click="confirmDeleteStep(step.id)"
                      class="p-1 hover:bg-red-500/50 rounded"
                      title="Delete step"
                    >
                      <i class="fa-solid fa-trash text-xs"></i>
                    </button>
                  </div>
                </div>
                <h4 class="font-semibold mt-2 truncate" :title="step.name">{{ step.name }}</h4>
              </div>

              <!-- Step Content - Sections List -->
              <div class="flex-1 p-3 overflow-y-auto">
                <div v-if="getSectionsForStep(step).length > 0" class="space-y-2">
                  <div
                    v-for="section in getSectionsForStep(step)"
                    :key="section.id"
                    draggable="true"
                    @dragstart="onSectionDragStart(section.id)"
                    @dragend="onSectionDragEnd"
                    class="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm cursor-move hover:bg-gray-100 hover:border-primary-300 transition"
                  >
                    <div class="flex items-center gap-2">
                      <i class="fa-solid fa-grip-vertical text-gray-400"></i>
                      <span class="font-medium text-gray-700 truncate">{{ section.name }}</span>
                    </div>
                  </div>
                </div>
                <div v-else class="flex flex-col items-center justify-center h-full text-center py-4">
                  <div class="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                    <i class="fa-solid fa-inbox text-gray-400"></i>
                  </div>
                  <p class="text-xs text-gray-400">Drop sections here</p>
                </div>
              </div>

              <!-- Step Footer -->
              <div class="px-3 py-2 bg-gray-50 border-t border-gray-100 text-center">
                <span class="text-xs text-gray-500">
                  <i class="fa-solid fa-file-lines mr-1"></i>
                  {{ getSectionsForStep(step).length }} sections
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="orderedSteps.length === 0" class="bg-white rounded-lg border border-gray-200 shadow-md p-8 text-center">
          <i class="fa-solid fa-layer-group text-4xl text-gray-300 mb-4"></i>
          <h3 class="text-lg font-semibold text-gray-700 mb-2">No steps created yet</h3>
          <p class="text-gray-600 mb-4">Create steps to organize your form into a multi-step workflow</p>
          <button
            @click="openStepForm"
            class="btn-primary bg-primary-600 text-white px-6 py-2 rounded hover:bg-primary-700 transition"
          >
            <i class="fa-solid fa-plus mr-2"></i> Create First Step
          </button>
        </div>

        <!-- Help Text -->
        <div class="bg-primary-50 border border-primary-200 rounded-lg p-4">
          <div class="flex items-start gap-3">
            <i class="fa-solid fa-lightbulb text-primary text-xl mt-0.5"></i>
            <div>
              <h5 class="font-semibold text-primary-800">How to use Steps</h5>
              <ul class="text-sm text-primary-700 mt-2 space-y-1">
                <li><i class="fa-solid fa-circle text-[4px] mr-2 align-middle"></i>Click the <strong>+</strong> card to create a new step</li>
                <li><i class="fa-solid fa-circle text-[4px] mr-2 align-middle"></i>Use the <strong>arrows</strong> to reorder steps</li>
                <li><i class="fa-solid fa-circle text-[4px] mr-2 align-middle"></i><strong>Drag sections</strong> from the pool above and drop them onto a step</li>
                <li><i class="fa-solid fa-circle text-[4px] mr-2 align-middle"></i>Use the <strong>trash icon</strong> to delete a step</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      </template>
    </div>

    <!-- Create Section Modal -->
    <div
      v-if="showSectionForm"
      class="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50"
      @click.self="closeSectionForm">
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div class="px-6 py-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold text-gray-800">Create New Section</h3>
        </div>

        <form @submit.prevent="createSection" class="px-6 py-4 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Section Name <span class="text-red-500">*</span>
            </label>
            <input
              v-model="sectionForm.name"
              type="text"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g., Personal Information"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"> Description </label>
            <textarea
              v-model="sectionForm.description"
              rows="4"
              class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Describe what information this section collects"
            ></textarea>
          </div>

          <div class="text-sm text-gray-600">
            <i class="fa-solid fa-info-circle mr-1"></i>
            Position: {{ sectionForm.position + 1 }}
          </div>
        </form>

        <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
          <button
            @click="closeSectionForm"
            type="button"
            class="btn-danger"
          >
            <i class="fas fa-times mr-1"></i>
            Cancel
          </button>
          <button
            @click="createSection"
            :disabled="sectionLoading || !sectionForm.name"
            class="btn-primary px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i v-if="sectionLoading" class="fa-solid fa-spinner fa-spin mr-2"></i>
            {{ sectionLoading ? 'Creating...' : 'Create Section' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Update Section Modal -->
    <div
        v-if="showEditSectionForm"
        class="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50"
        @click.self="closeEditSectionForm">
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div class="px-6 py-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold text-gray-800">Edit Section</h3>
        </div>

        <form @submit.prevent="updateSection" class="px-6 py-4 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Section Name <span class="text-red-500">*</span>
            </label>
            <input
                v-model="editSectionForm.name"
                type="text"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., Personal Information"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"> Description </label>
            <textarea
                v-model="editSectionForm.description"
                rows="4"
                class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Describe what information this section collects"
            ></textarea>
          </div>
        </form>

        <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
          <button
              @click="closeEditSectionForm"
              type="button"
              class="btn-danger"
          >
            <i class="fas fa-times mr-1"></i>
            Cancel
          </button>
          <button
              @click="updateSection"
              :disabled="sectionLoading || !editSectionForm.name"
              class="btn-primary px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i v-if="sectionLoading" class="fa-solid fa-spinner fa-spin mr-2"></i>
            {{ sectionLoading ? 'Updating...' : 'Update Section' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Add Field Modal -->
    <div
      v-if="showFieldForm"
      class="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50"
      @click.self="closeFieldForm"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div class="px-6 py-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold text-gray-800">Add Field to Section</h3>
        </div>

        <form @submit.prevent="createField" class="px-6 py-4 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Select Field <span class="text-red-500">*</span>
            </label>
            <select
              v-model="fieldForm.fieldId"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500" @change="loadRestrictions">
              <option value="">-- Choose a field --</option>
              <option v-for="field in store.fields" :key="field.id" :value="field.id">
                {{ field.label }} ({{ field.type }})
              </option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              <i class="fa-solid fa-tag text-gray-400 mr-1"></i>
              Label
            </label>
            <input
              v-model="fieldForm.label"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Custom label for this field (optional)"
            />
            <p class="text-xs text-gray-500 mt-1">Override the default field label for this form</p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              <i class="fa-solid fa-tag text-gray-400 mr-1"></i>
              Placeholder
            </label>
            <input
                v-model="fieldForm.placeholder"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Custom placeholder for this field (optional)"
            />
            <p class="text-xs text-gray-500 mt-1">Override the default field placeholder for this form</p>
          </div>

          <div class="flex items-center gap-4">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="fieldForm.isMandatory"
                type="checkbox"
                class="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span class="text-sm text-gray-700">Mandatory field</span>
            </label>

            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="fieldForm.isActive"
                type="checkbox"
                class="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span class="text-sm text-gray-700">Active</span>
            </label>
          </div>

          <div class="text-sm text-gray-600">
            <i class="fa-solid fa-info-circle mr-1"></i>
            Position: {{ fieldForm.position + 1 }}
          </div>
        </form>

        <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
          <button
            @click="closeFieldForm"
            type="button"
            class="btn-danger"
          >
            <i class="fas fa-times mr-1"></i>
            Cancel
          </button>
          <button
            @click="createField"
            :disabled="fieldLoading || !fieldForm.fieldId"
            class="btn-primary px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i v-if="fieldLoading" class="fa-solid fa-spinner fa-spin mr-2"></i>
            {{ fieldLoading ? 'Adding...' : 'Add Field' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Edit Field Modal -->
  <div
    v-if="showEditFieldForm"
    class="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50"
    @click.self="closeEditFieldForm"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
      <div class="px-6 py-4 border-b border-gray-200">
        <h3 class="text-lg font-semibold text-gray-800">
          <i class="fa-solid fa-pen text-gray-500 mr-2"></i>
          Edit Field
        </h3>
      </div>

      <form @submit.prevent="updateField" class="px-6 py-4 space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Select Field <span class="text-red-500">*</span>
          </label>
          <select
            v-model="editFieldForm.fieldId"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">-- Choose a field --</option>
            <option v-for="field in store.fields" :key="field.id" :value="field.id">
              {{ field.label }} ({{ field.type }})
            </option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-tag text-gray-400 mr-1"></i>
            Label
          </label>
          <input
            v-model="editFieldForm.label"
            type="text"
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Custom label for this field (optional)"
          />
          <p class="text-xs text-gray-500 mt-1">Override the default field label for this form</p>
        </div>


        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-tag text-gray-400 mr-1"></i>
            Placeholder
          </label>
          <input
              v-model="editFieldForm.placeholder"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Custom placeholder for this field (optional)"
          />
          <p class="text-xs text-gray-500 mt-1">Override the default field placeholder for this form</p>
        </div>

        <div class="flex items-center gap-4">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="editFieldForm.isMandatory"
              type="checkbox"
              class="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span class="text-sm text-gray-700">Mandatory field</span>
          </label>

          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="editFieldForm.isActive"
              type="checkbox"
              class="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span class="text-sm text-gray-700">Active</span>
          </label>
        </div>
      </form>

      <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
        <button
          @click="closeEditFieldForm"
          type="button"
          class="btn-danger"
        >
          <i class="fas fa-times mr-1"></i>
          Cancel
        </button>
        <button
          @click="updateField"
          :disabled="fieldLoading || !editFieldForm.fieldId"
          class="btn-primary px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i v-if="fieldLoading" class="fa-solid fa-spinner fa-spin mr-2"></i>
          {{ fieldLoading ? 'Saving...' : 'Save Changes' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Field Restrictions Modal -->
  <div
    v-if="showFieldRestrictionForm"
    class="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50"
    @click.self="closeFieldRestrictionForm"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
      <div class="px-6 py-4 border-b border-gray-200">
        <h3 class="text-lg font-semibold text-gray-800">
          <i class="fa-solid fa-cog text-gray-500 mr-2"></i>
          Field Restrictions
        </h3>
        <p class="text-sm text-gray-500 mt-1">Set validation rules for this field</p>
      </div>

      <form @submit.prevent="saveFieldRestrictions" class="px-6 py-4 space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-text-width text-gray-400 mr-1"></i>
            Minimum Length
          </label>
          <input
            v-model.number="fieldRestrictionForm.min"
            type="number"
            min="0"
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="e.g., 3"
          />
          <p class="text-xs text-gray-500 mt-1">Minimum number of characters required</p>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-text-width text-gray-400 mr-1"></i>
            Maximum Length
          </label>
          <input
            v-model.number="fieldRestrictionForm.max"
            type="number"
            min="0"
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="e.g., 100"
          />
          <p class="text-xs text-gray-500 mt-1">Maximum number of characters allowed</p>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-code text-gray-400 mr-1"></i>
            Regex Pattern
            <span class="text-gray-400 text-xs ml-1">(Optional)</span>
          </label>
          <input
            v-model="fieldRestrictionForm.regex"
            type="text"
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono text-sm"
            placeholder="e.g., ^[A-Za-z]+$"
          />
          <p class="text-xs text-gray-500 mt-1">Regular expression pattern for validation</p>
        </div>
      </form>

      <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
        <button
          @click="closeFieldRestrictionForm"
          type="button"
          class="btn-danger"
        >
          <i class="fas fa-times mr-1"></i>
          Cancel
        </button>
        <button
          @click="saveFieldRestrictions"
          :disabled="fieldLoading"
          class="btn-primary px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i v-if="fieldLoading" class="fa-solid fa-spinner fa-spin mr-2"></i>
          {{ fieldLoading ? 'Saving...' : 'Save Restrictions' }}
        </button>
      </div>
    </div>
  </div>

  <AppModal v-model="showRemoveFieldForm" xl2>
    <div class="p-2" id="delete-confirm">
      <div class="text-center py-4">
        <div
          class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4"
        >
          <i class="fa-solid fa-exclamation-triangle text-red-600 text-xl"></i>
        </div>
        <h3 class="text-lg font-bold text-gray-900 mb-2">Delete Form Field</h3>
        <p class="text-sm text-gray-600 mb-6">
          Are you sure you want to delete this form field? This action cannot be undone.
        </p>

        <div class="flex items-center justify-center space-x-3">
          <button
            type="button"
            @click="showRemoveFieldForm = false"
            class="btn-danger"
          >
            <i class="fas fa-times mr-1"></i>
            Cancel
          </button>
          <button
            type="button"
            @click="removeField"
            class="btn-primary px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition"
          >
            <i class="fa-solid fa-trash mr-1"></i>
            <span v-if="!loading">Confirm Delete</span>
            <span v-else>Deleting...</span>
          </button>
        </div>
      </div>
    </div>
  </AppModal>

  <AppModal v-model="showRemoveSectionForm" xl2>
    <div class="p-2" id="delete-confirm">
      <div class="text-center py-4">
        <div
            class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4"
        >
          <i class="fa-solid fa-exclamation-triangle text-red-600 text-xl"></i>
        </div>
        <h3 class="text-lg font-bold text-gray-900 mb-2">Delete Form Section</h3>
        <p class="text-sm text-gray-600 mb-6">
          Are you sure you want to delete this section? This action cannot be undone.
        </p>

        <div class="flex items-center justify-center space-x-3">
          <button
              type="button"
              @click="showRemoveSectionForm = false"
              class="btn-danger"
          >
            <i class="fas fa-times mr-1"></i>
            Cancel
          </button>
          <button
              type="button"
              @click="removeSection"
              class="btn-primary px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition"
          >
            <i class="fa-solid fa-trash mr-1"></i>
            <span v-if="!loading">Confirm Delete</span>
            <span v-else>Deleting...</span>
          </button>
        </div>
      </div>
    </div>
  </AppModal>

  <!-- Create Attachment Modal -->
  <div
    v-if="showAttachmentForm"
    class="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50"
    @click.self="closeAttachmentForm"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
      <div class="px-6 py-4 border-b border-gray-200">
        <h3 class="text-lg font-semibold text-gray-800">
          <i class="fa-solid fa-paperclip mr-2 text-gray-500"></i>
          Add Attachment Requirement
        </h3>
      </div>

      <form @submit.prevent="createAttachment" class="px-6 py-4 space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Label <span class="text-red-500">*</span>
          </label>
          <input
            v-model="attachmentForm.label"
            type="text"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="e.g., Certificate of Incorporation"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            v-model="attachmentForm.description"
            rows="3"
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Brief description of what document is required (optional)"
          ></textarea>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-file-arrow-up text-gray-400 mr-1"></i>
            Maximum File Size (MB) <span class="text-red-500">*</span>
          </label>
          <input
            v-model.number="attachmentForm.maxSize"
            type="number"
            min="1"
            max="50"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="e.g., 5"
          />
          <p class="text-xs text-gray-500 mt-1">Maximum allowed file size in megabytes (1-50 MB)</p>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-file-lines text-gray-400 mr-1"></i>
            File Type <span class="text-red-500">*</span>
          </label>
          <select
            v-model="attachmentForm.fileType"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">-- Select file type --</option>
            <option
              v-for="fileType in availableFileTypes"
              :key="fileType.value"
              :value="fileType.value"
            >
              {{ fileType.label }}
            </option>
          </select>
          <p class="text-xs text-gray-500 mt-1">Select the allowed file type for this attachment</p>
        </div>

        <div class="flex items-center gap-2">
          <input
            v-model="attachmentForm.isMandatory"
            type="checkbox"
            id="attachmentMandatory"
            class="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <label for="attachmentMandatory" class="text-sm text-gray-700">
            <i class="fa-solid fa-asterisk text-red-500 text-xs mr-1"></i>
            This attachment is mandatory
          </label>
        </div>
      </form>

      <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
        <button
          @click="closeAttachmentForm"
          type="button"
          class="btn-danger"
        >
          <i class="fas fa-times mr-1"></i>
          Cancel
        </button>
        <button
          @click="createAttachment"
          :disabled="attachmentLoading || !attachmentForm.label"
          class="btn-primary px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i v-if="attachmentLoading" class="fa-solid fa-spinner fa-spin mr-2"></i>
          {{ attachmentLoading ? 'Adding...' : 'Add Attachment' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Edit Attachment Modal -->
  <div
    v-if="showEditAttachmentForm"
    class="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50"
    @click.self="closeEditAttachmentForm"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
      <div class="px-6 py-4 border-b border-gray-200">
        <h3 class="text-lg font-semibold text-gray-800">
          <i class="fa-solid fa-pen mr-2 text-gray-500"></i>
          Edit Attachment
        </h3>
      </div>

      <form @submit.prevent="updateAttachment" class="px-6 py-4 space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Label <span class="text-red-500">*</span>
          </label>
          <input
            v-model="updateAttachmentForm.label"
            type="text"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="e.g., Certificate of Incorporation"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            v-model="updateAttachmentForm.description"
            rows="3"
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Brief description of what document is required (optional)"
          ></textarea>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-file-arrow-up text-gray-400 mr-1"></i>
            Maximum File Size (MB) <span class="text-red-500">*</span>
          </label>
          <input
            v-model.number="updateAttachmentForm.maxSize"
            type="number"
            min="1"
            max="50"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="e.g., 5"
          />
          <p class="text-xs text-gray-500 mt-1">Maximum allowed file size in megabytes (1-50 MB)</p>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-file-lines text-gray-400 mr-1"></i>
            File Type <span class="text-red-500">*</span>
          </label>
          <select
            v-model="updateAttachmentForm.fileType"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">-- Select file type --</option>
            <option
              v-for="fileType in availableFileTypes"
              :key="fileType.value"
              :value="fileType.value"
            >
              {{ fileType.label }}
            </option>
          </select>
          <p class="text-xs text-gray-500 mt-1">Select the allowed file type for this attachment</p>
        </div>

        <div class="flex items-center gap-2">
          <input
            v-model="updateAttachmentForm.isMandatory"
            type="checkbox"
            id="editAttachmentMandatory"
            class="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <label for="editAttachmentMandatory" class="text-sm text-gray-700">
            <i class="fa-solid fa-asterisk text-red-500 text-xs mr-1"></i>
            This attachment is mandatory
          </label>
        </div>
      </form>

      <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
        <button
          @click="closeEditAttachmentForm"
          type="button"
          class="btn-danger"
        >
          <i class="fas fa-times mr-1"></i>
          Cancel
        </button>
        <button
          @click="updateAttachment"
          :disabled="attachmentLoading || !updateAttachmentForm.label"
          class="btn-primary px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i v-if="attachmentLoading" class="fa-solid fa-spinner fa-spin mr-2"></i>
          {{ attachmentLoading ? 'Saving...' : 'Save Changes' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Remove Attachment Confirmation Modal -->
  <AppModal v-model="showRemoveAttachmentForm" xl2>
    <div class="p-2">
      <div class="text-center py-4">
        <div
          class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4"
        >
          <i class="fa-solid fa-exclamation-triangle text-red-600 text-xl"></i>
        </div>
        <h3 class="text-lg font-bold text-gray-900 mb-2">Remove Attachment</h3>
        <p class="text-sm text-gray-600 mb-6">
          Are you sure you want to remove this attachment requirement? This action cannot be undone.
        </p>

        <div class="flex items-center justify-center space-x-3">
          <button
            type="button"
            @click="showRemoveAttachmentForm = false"
            class="btn-danger"
          >
            <i class="fas fa-times mr-1"></i>
            Cancel
          </button>
          <button
            type="button"
            @click="removeAttachment"
            class="btn-primary px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition"
          >
            <i class="fa-solid fa-trash mr-1"></i>
            <span v-if="!attachmentLoading">Confirm Remove</span>
            <span v-else>Removing...</span>
          </button>
        </div>
      </div>
    </div>
  </AppModal>

  <!-- Create Step Modal -->
  <div
    v-if="showStepForm"
    class="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50"
    @click.self="closeStepForm"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
      <div class="px-6 py-4 border-b border-gray-200">
        <h3 class="text-lg font-semibold text-gray-800">
          <i class="fa-solid fa-layer-group mr-2 text-primary"></i>
          Create New Step
        </h3>
      </div>

      <form @submit.prevent="createStep" class="px-6 py-4 space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Step Name <span class="text-red-500">*</span>
          </label>
          <input
            v-model="stepForm.name"
            type="text"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="e.g., Basic Information"
          />
        </div>

        <div class="text-sm text-gray-600 bg-gray-50 p-3 rounded">
          <i class="fa-solid fa-info-circle mr-1 text-primary"></i>
          This will be <strong>Step {{ orderedSteps.length + 1 }}</strong>. You can reorder steps after creation.
        </div>
      </form>

      <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
        <button
          @click="closeStepForm"
          type="button"
          class="btn-danger"
        >
          <i class="fas fa-times mr-1"></i>
          Cancel
        </button>
        <button
          @click="createStep"
          :disabled="stepLoading || !stepForm.name"
          class="btn-primary px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i v-if="stepLoading" class="fa-solid fa-spinner fa-spin mr-2"></i>
          {{ stepLoading ? 'Creating...' : 'Create Step' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Delete Step Confirmation Modal -->
  <AppModal v-model="showDeleteStepConfirm" xl2>
    <div class="p-2">
      <div class="text-center py-4">
        <div
          class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4"
        >
          <i class="fa-solid fa-exclamation-triangle text-red-600 text-xl"></i>
        </div>
        <h3 class="text-lg font-bold text-gray-900 mb-2">Delete Step</h3>
        <p class="text-sm text-gray-600 mb-6">
          Are you sure you want to delete this step? Any sections assigned to it will become unassigned. This action cannot be undone.
        </p>

        <div class="flex items-center justify-center space-x-3">
          <button
            type="button"
            @click="showDeleteStepConfirm = false"
            class="btn-danger"
          >
            <i class="fas fa-times mr-1"></i>
            Cancel
          </button>
          <button
            type="button"
            @click="deleteStep"
            class="btn-primary px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition"
          >
            <i class="fa-solid fa-trash mr-1"></i>
            <span v-if="!stepLoading">Confirm Delete</span>
            <span v-else>Deleting...</span>
          </button>
        </div>
      </div>
    </div>
  </AppModal>
</template>

<style scoped></style>
